import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api/api';

// Async Thunk to fetch translators with filters and pagination.
export const fetchTranslators = createAsyncThunk(
  'translators/fetchTranslators',
  async (filters = {}, { rejectWithValue }) => {
    try {
      // Pass filters as query parameters; backend should return { data, pagination }
      const response = await api.get('/translators', {
        params: filters,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch translators');
    }
  }
);

export const createTranslator = createAsyncThunk(
  'translators/createTranslator',
  async (translatorData, { rejectWithValue }) => {
    try {
      const response = await api.post('/translators', translatorData);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to create translator');
    }
  }
);

export const updateTranslator = createAsyncThunk(
  'translators/updateTranslator',
  async ({ id, ...translatorData }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/translators/${id}`, {
        ...translatorData,
        _method: 'PATCH', // Laravel compatibility
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Update failed');
    }
  }
);

export const deleteTranslator = createAsyncThunk(
  'translators/deleteTranslator',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/translators/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Delete failed');
    }
  }
);

const translatorSlice = createSlice({
  name: 'translators',
  initialState: {
    translators: [],
    pagination: {
      current_page: 1,
      per_page: 10,
      total: 0,
      last_page: 1,
    },
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch Translators
      .addCase(fetchTranslators.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTranslators.fulfilled, (state, action) => {
        state.loading = false;
        // Expect backend response to include data and pagination info.
        state.translators = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchTranslators.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create Translator
      .addCase(createTranslator.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createTranslator.fulfilled, (state, action) => {
        state.loading = false;
        state.translators.push(action.payload);
      })
      .addCase(createTranslator.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update Translator
      .addCase(updateTranslator.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTranslator.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.translators.findIndex((t) => t.id === action.payload.id);
        if (index !== -1) {
          state.translators[index] = action.payload;
        }
      })
      .addCase(updateTranslator.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete Translator
      .addCase(deleteTranslator.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteTranslator.fulfilled, (state, action) => {
        state.loading = false;
        state.translators = state.translators.filter((t) => t.id !== action.payload);
      })
      .addCase(deleteTranslator.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export default translatorSlice.reducer;
